import React, { useState } from 'react';
import { usePersonaGeneration } from '../../hooks/usePersonaGeneration';

interface PersonaGeneratorProps {
  projectId: string;
  onComplete?: () => void;
}

export const PersonaGenerator: React.FC<PersonaGeneratorProps> = ({
  projectId,
  onComplete,
}) => {
  const [panelType, setPanelType] = useState<'brand' | 'category' | 'b2b'>('brand');
  const [count, setCount] = useState<number>(7);
  const [medianHHI, setMedianHHI] = useState<string>('');

  const { generate, loading, progress, error, success } = usePersonaGeneration();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (count < 1 || count > 20) {
      alert('Count must be between 1 and 20');
      return;
    }

    const params: any = {
      projectId,
      panelType,
      count,
    };

    if (medianHHI && medianHHI.trim()) {
      const parsedHHI = parseInt(medianHHI.replace(/,/g, ''), 10);
      if (!isNaN(parsedHHI) && parsedHHI > 0) {
        params.medianHHI = parsedHHI;
      }
    }

    await generate(params);

    if (onComplete) {
      onComplete();
    }
  };

  if (success) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-accent-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Personas Generated Successfully!
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Your personas have been created and are now available below.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          View Personas
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Generate Personas</h3>
        <p className="mt-1 text-sm text-gray-500">
          Create AI-powered consumer personas for your project
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Panel Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Panel Type
          </label>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="brand"
                  name="panelType"
                  type="radio"
                  className="focus:ring-primary-500 h-4 w-4 text-primary-500 border-gray-300"
                  checked={panelType === 'brand'}
                  onChange={() => setPanelType('brand')}
                  disabled={loading}
                />
              </div>
              <div className="ml-3">
                <label htmlFor="brand" className="font-medium text-gray-700">
                  Brand Panel
                </label>
                <p className="text-gray-500 text-sm">
                  Personas with varying relationships to your brand (superfans, users, skeptics, etc.)
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="category"
                  name="panelType"
                  type="radio"
                  className="focus:ring-primary-500 h-4 w-4 text-primary-500 border-gray-300"
                  checked={panelType === 'category'}
                  onChange={() => setPanelType('category')}
                  disabled={loading}
                />
              </div>
              <div className="ml-3">
                <label htmlFor="category" className="font-medium text-gray-700">
                  Category Panel
                </label>
                <p className="text-gray-500 text-sm">
                  Personas representing your product category without specific brand focus
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="b2b"
                  name="panelType"
                  type="radio"
                  className="focus:ring-primary-500 h-4 w-4 text-primary-500 border-gray-300"
                  checked={panelType === 'b2b'}
                  onChange={() => setPanelType('b2b')}
                  disabled={loading}
                />
              </div>
              <div className="ml-3">
                <label htmlFor="b2b" className="font-medium text-gray-700">
                  B2B Panel
                </label>
                <p className="text-gray-500 text-sm">
                  Business personas with professional context and buying authority
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Count */}
        <div>
          <label htmlFor="count" className="block text-sm font-medium text-gray-700 mb-2">
            Number of Personas (1-20)
          </label>
          <input
            type="number"
            id="count"
            min="1"
            max="20"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)}
            disabled={loading}
          />
          <p className="mt-1 text-sm text-gray-500">
            Recommended: 7 personas for most projects
          </p>
        </div>

        {/* Median HHI */}
        <div>
          <label htmlFor="medianHHI" className="block text-sm font-medium text-gray-700 mb-2">
            Median Household Income (Optional)
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="text"
              id="medianHHI"
              className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="75,000"
              value={medianHHI}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9,]/g, '');
                setMedianHHI(value);
              }}
              disabled={loading}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Target median household income for the panel
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Generation Failed
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error.message}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State with Progress */}
        {loading && (
          <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="animate-spin h-5 w-5 text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  Generating Personas...
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  This may take 1-3 minutes. Please don't close this page.
                </div>
                <div className="mt-3">
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-blue-600">{Math.round(progress)}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Personas'}
          </button>
        </div>
      </form>
    </div>
  );
};
